import type { InterviewTypeDetailDto, InterviewTypeVersionDetailDto } from '@sara/api-client';
import { Alert, AlertDescription, AlertTitle } from '@sara/ui/components/alert';
import { Badge } from '@sara/ui/components/badge';
import { Button } from '@sara/ui/components/button';
import { Plus, TriangleAlert } from 'lucide-react';
import { Link } from '@tanstack/react-router';
import { PageHeader } from '../../../../components/page-header';
import { splitConsentDetails } from '@sara/message-schemas';
import { showOrgLogoFromApi, SHOW_ORG_LOGO_LABELS } from '../../lib/show-org-logo';
import { MarkdownText } from '../../../../components/markdown-text';
import { CopyFieldButton } from '../CopyFieldButton';
import { PlatformVariablesList } from '../PlatformVariablesList';

type VariableDefinition = NonNullable<
  InterviewTypeVersionDetailDto['dynamicVariableDefinitions']
>[number];

const VARIABLE_TYPE_WORD: Record<string, string> = {
  TEXT: 'Free text',
  NUMBER: 'Number',
  CURRENCY: 'Amount',
  DATE: 'Date',
};

/** A human, scannable one-line summary of a declared variable's shape. */
function describeVariable(def: VariableDefinition): string {
  const parts: string[] = [];
  parts.push(
    def.allowedValues.length
      ? `Choose from ${def.allowedValues.join(', ')}`
      : VARIABLE_TYPE_WORD[def.type]
  );
  if (def.defaultValue) parts.push(`default ${def.defaultValue}`);
  return parts.join(' · ');
}

interface InterviewTypeDetailProps {
  interviewType: InterviewTypeDetailDto;
  orgSlug: string;
  onCreateVersion: () => void;
  onEdit: () => void;
  onArchive: () => void;
  onUnarchive: () => void;
  isArchivePending: boolean;
}

/** Shared label treatment for the fields inside the Voice agent card. */
const fieldLabelClassName =
  'mb-1.5 text-[12px] uppercase tracking-[0.08em] text-muted-foreground/70';

/** Consent statement: null = platform default; otherwise the custom markdown (rendered). */
function ConsentStatementValue({ value }: { value: string | null }) {
  if (value === null || value === undefined)
    return <span className="text-muted-foreground">Platform default</span>;
  return <MarkdownText inline>{value}</MarkdownText>;
}

/**
 * "Learn more" details: null = platform default; otherwise the custom markdown
 * rendered the same way the candidate sees it — `## ` lines become section
 * headings, bodies render bold/lists.
 */
function ConsentDetailsValue({ value }: { value: string | null }) {
  if (value === null || value === undefined)
    return <span className="text-muted-foreground">Platform default</span>;
  const sections = splitConsentDetails(value);
  return (
    <div className="space-y-3">
      {sections.map((section) => (
        <div key={`${section.heading} ${section.body}`}>
          {section.heading && <p className="font-medium text-foreground">{section.heading}</p>}
          <MarkdownText className="text-muted-foreground">{section.body}</MarkdownText>
        </div>
      ))}
    </div>
  );
}

/** Secondary-link override: null = default, "" = hidden, otherwise the custom text. */
function LinkOverrideValue({
  value,
  defaultLabel,
}: {
  value: string | null;
  defaultLabel: string;
}) {
  if (value === null || value === undefined)
    return <span className="text-muted-foreground">{defaultLabel}</span>;
  if (value === '') return <span className="text-muted-foreground">Hidden</span>;
  return <span className="whitespace-pre-wrap">{value}</span>;
}

function MetaItem({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <dt className="text-[11px] font-medium uppercase tracking-[0.06em] text-muted-foreground/80">
        {label}
      </dt>
      <dd className="text-[13.5px] text-foreground">{children}</dd>
    </div>
  );
}

export function InterviewTypeDetail({
  interviewType,
  orgSlug,
  onCreateVersion,
  onEdit,
  onArchive,
  onUnarchive,
  isArchivePending,
}: InterviewTypeDetailProps) {
  const latestVersion: InterviewTypeVersionDetailDto | undefined = interviewType.versions[0];
  const isArchived = interviewType.archivedAt !== null;
  const pillButtonClass =
    'h-8 px-3 text-xs [&_svg]:size-3.5 sm:h-9 sm:px-4 sm:text-sm sm:[&_svg]:size-4';

  return (
    <div className="space-y-12">
      <PageHeader
        size="compact"
        title={interviewType.name}
        description={interviewType.description}
        meta={
          <div className="flex items-center gap-2">
            {latestVersion && (
              <span className="inline-flex items-center gap-1.5 rounded-full border border-border/70 bg-card px-2.5 py-1 text-[11px] font-medium text-muted-foreground">
                <span className="h-1 w-1 rounded-full bg-primary" aria-hidden />
                <span className="tabular-nums">v{latestVersion.version}</span>
              </span>
            )}
            {isArchived && (
              <Badge variant="outline" className="rounded-full">
                Archived
              </Badge>
            )}
          </div>
        }
        action={
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              shape="pill"
              size="sm"
              className={pillButtonClass}
              onClick={onEdit}
            >
              Edit
            </Button>
            {isArchived ? (
              <Button
                variant="outline"
                shape="pill"
                size="sm"
                className={pillButtonClass}
                onClick={onUnarchive}
                disabled={isArchivePending}
              >
                Unarchive
              </Button>
            ) : (
              <Button
                variant="outline"
                shape="pill"
                size="sm"
                className={pillButtonClass}
                onClick={onArchive}
                disabled={isArchivePending}
              >
                Archive
              </Button>
            )}
            <Button
              variant="outline"
              shape="pill"
              size="sm"
              className={pillButtonClass}
              onClick={onCreateVersion}
              disabled={isArchived}
            >
              <Plus strokeWidth={1.6} />
              New version
            </Button>
          </div>
        }
      />

      {/* Meta strip — quiet, hairline-bordered */}
      <dl className="grid grid-cols-2 gap-x-10 gap-y-6 border-y border-border/70 py-6 sm:grid-cols-4">
        <MetaItem label="Slug">
          <span className="font-mono text-[13px] text-foreground">{interviewType.slug}</span>
        </MetaItem>
        <MetaItem label="Created">
          {new Date(interviewType.createdAt).toLocaleDateString(undefined, {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          })}
        </MetaItem>
        <MetaItem label="Versions">
          <span className="tabular-nums">{interviewType.versions.length}</span>
        </MetaItem>
        {latestVersion && (
          <MetaItem label="Latest version">
            v{latestVersion.version} ·{' '}
            {new Date(latestVersion.createdAt).toLocaleDateString(undefined, {
              month: 'short',
              day: 'numeric',
            })}
          </MetaItem>
        )}
      </dl>

      {/* Voice agent — the ElevenLabs agent that runs this interview. The prompt
          + first message are read live from the agent (source of truth). Agent ID,
          system prompt, and first message share one bordered card with a single
          header and consistent per-field treatment. */}
      {interviewType.elevenlabsAgentId ? (
        <section className="space-y-5">
          <div className="space-y-1">
            <h2 className="font-display text-2xl font-medium tracking-tight text-foreground">
              Voice agent
            </h2>
            <p className="text-xs text-muted-foreground/70">
              Live from ElevenLabs. Edit the template to change the prompt.
            </p>
          </div>
          <div className="divide-y divide-border/70 overflow-hidden rounded-xl border border-border/70 bg-card">
            <div className="relative px-5 py-4 pr-14">
              <p className={fieldLabelClassName}>Agent ID</p>
              <p className="font-mono text-[12.5px] leading-relaxed text-foreground/90 break-all">
                {interviewType.elevenlabsAgentId}
              </p>
              <CopyFieldButton value={interviewType.elevenlabsAgentId} label="agent ID" />
            </div>
            {interviewType.systemPrompt !== null ? (
              <div className="px-5 py-4" data-testid="agent-system-prompt">
                <p className={fieldLabelClassName}>System prompt</p>
                <p className="whitespace-pre-wrap break-words font-mono text-[12.5px] leading-relaxed text-foreground/90">
                  {interviewType.systemPrompt || (
                    <span className="italic text-muted-foreground">No system prompt set.</span>
                  )}
                </p>
              </div>
            ) : (
              <div className="px-5 py-4">
                <p className="text-sm text-muted-foreground">
                  Couldn&rsquo;t load the agent&rsquo;s live prompt from ElevenLabs right now.
                </p>
              </div>
            )}
            {interviewType.firstMessage !== null && (
              <div className="px-5 py-4" data-testid="agent-first-message">
                <p className={fieldLabelClassName}>First message</p>
                <p className="whitespace-pre-wrap break-words text-[13px] leading-relaxed text-foreground/90">
                  {interviewType.firstMessage || (
                    <span className="italic text-muted-foreground">
                      No opening line — the agent opens on its own.
                    </span>
                  )}
                </p>
              </div>
            )}
          </div>
        </section>
      ) : (
        <Alert variant="destructive" className="bg-destructive/10">
          <TriangleAlert className="h-4 w-4" />
          <AlertTitle>Missing ElevenLabs agent ID</AlertTitle>
          <AlertDescription>
            This template needs an ElevenLabs agent ID to run interviews. Edit the template to add
            one.
          </AlertDescription>
        </Alert>
      )}

      {/* Variables — pre-call inputs the agent receives at conversation start.
          Kept separate from Analysis, which is post-call output. */}
      {latestVersion && (
        <section className="space-y-5">
          <div className="space-y-1">
            <h2 className="font-display text-2xl font-medium tracking-tight text-foreground">
              Dynamic variables
            </h2>
            <p className="text-xs text-muted-foreground/70">
              Passed to the agent at the start of the call.
            </p>
          </div>
          <PlatformVariablesList />
          <div className="space-y-2.5">
            <p className="text-[12px] font-medium text-foreground/80">Custom</p>
            {latestVersion.dynamicVariableDefinitions?.length ? (
              <ul className="space-y-7" data-testid="dynamic-variable-definitions">
                {latestVersion.dynamicVariableDefinitions.map((def) => (
                  <li key={def.key} className="space-y-1">
                    <div className="flex items-baseline gap-2.5">
                      <span className="text-[15px] font-semibold text-foreground">{def.name}</span>
                      <code className="font-mono text-[12.5px] text-muted-foreground">
                        {'{{'}
                        {def.key}
                        {'}}'}
                      </code>
                      {def.required ? (
                        <span className="ml-auto text-[10.5px] font-medium uppercase tracking-[0.08em] text-muted-foreground/70">
                          Required
                        </span>
                      ) : null}
                    </div>
                    <p className="text-[12.5px] text-muted-foreground/70">
                      {describeVariable(def)}
                    </p>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-muted-foreground">
                No custom variables. This template takes no inputs beyond the platform&rsquo;s.
              </p>
            )}
          </div>
        </section>
      )}

      {/* Analysis — extraction and rubric attached to the latest version (post-call) */}
      {latestVersion && (
        <section className="space-y-5">
          <div className="space-y-1">
            <h2 className="font-display text-2xl font-medium tracking-tight text-foreground">
              Analysis
            </h2>
            <p className="text-xs text-muted-foreground/70">
              Runs on each conversation after the call.
            </p>
          </div>
          <dl className="grid grid-cols-1 gap-x-10 gap-y-6 sm:grid-cols-2">
            <MetaItem label="Extraction">
              {latestVersion.extraction ? (
                <Link
                  to="/orgs/$orgSlug/extractions/$extractionId"
                  params={{ orgSlug, extractionId: latestVersion.extraction.extractionId }}
                  className="text-primary underline-offset-4 hover:underline"
                >
                  {latestVersion.extraction.name} · v{latestVersion.extraction.version}
                </Link>
              ) : (
                <span className="text-muted-foreground">None</span>
              )}
            </MetaItem>
            <MetaItem label="Rubric">
              {latestVersion.rubric ? (
                <Link
                  to="/orgs/$orgSlug/rubrics/$rubricId"
                  params={{ orgSlug, rubricId: latestVersion.rubric.rubricId }}
                  className="text-primary underline-offset-4 hover:underline"
                >
                  {latestVersion.rubric.name} · v{latestVersion.rubric.version}
                </Link>
              ) : (
                <span className="text-muted-foreground">None</span>
              )}
            </MetaItem>
          </dl>
        </section>
      )}

      {/* Candidate experience — candidate-facing fields from the latest version */}
      {latestVersion && (
        <section className="space-y-5">
          <h2 className="font-display text-2xl font-medium tracking-tight text-foreground">
            Candidate experience
          </h2>
          <dl className="grid grid-cols-1 gap-x-10 gap-y-6 sm:grid-cols-2">
            <MetaItem label="Call title">{latestVersion.title}</MetaItem>
            <MetaItem label="Organization logo">
              {SHOW_ORG_LOGO_LABELS[showOrgLogoFromApi(latestVersion.showOrgLogo)]}
            </MetaItem>
            <MetaItem label="Lobby description">{latestVersion.description}</MetaItem>
            <MetaItem label="End-of-call notes">{latestVersion.endOfCallNotes}</MetaItem>
            <MetaItem label="Declined message">
              {/* Preserve authored line breaks; the raw **bold** markers are the
                  authored source (rendered as bold on the candidate screen). */}
              <span className="whitespace-pre-wrap">{latestVersion.declinedMessage}</span>
            </MetaItem>
            <MetaItem label="Consent statement">
              <ConsentStatementValue value={latestVersion.consentStatement} />
            </MetaItem>
            <MetaItem label="“Learn more” details">
              <ConsentDetailsValue value={latestVersion.consentDetails} />
            </MetaItem>
            <MetaItem label="Secondary link label">
              <LinkOverrideValue
                value={latestVersion.secondaryLinkLabel}
                defaultLabel='Default ("Request a live call instead")'
              />
            </MetaItem>
            <MetaItem label="Secondary link note">
              <LinkOverrideValue
                value={latestVersion.secondaryLinkDescription}
                defaultLabel="Default accommodation note"
              />
            </MetaItem>
          </dl>
        </section>
      )}

      {/* Versions */}
      <section className="space-y-5">
        <h2 className="font-display text-2xl font-medium tracking-tight text-foreground">
          Versions
        </h2>
        {interviewType.versions.length === 0 ? (
          <p className="text-sm text-muted-foreground">No versions have been created yet.</p>
        ) : (
          <ul className="divide-y divide-border/70 border-y border-border/70">
            {interviewType.versions.map((version) => {
              const isLatest = version.id === latestVersion?.id;
              return (
                <li
                  key={version.id}
                  className="flex items-center justify-between gap-6 py-4 text-sm"
                >
                  <div className="flex items-baseline gap-3">
                    <span className="font-display text-base text-foreground tabular-nums">
                      v{version.version}
                    </span>
                    {isLatest && (
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-accent/60 px-2 py-0.5 text-[11px] font-medium text-accent-foreground">
                        <span className="h-1 w-1 rounded-full bg-primary" aria-hidden />
                        Latest
                      </span>
                    )}
                  </div>
                  <span className="text-xs tabular-nums text-muted-foreground">
                    {new Date(version.createdAt).toLocaleDateString(undefined, {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    })}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
}
